// src/components/dashboard/SingleDateNavigator.js
// 單日日期導航器 — 左右箭頭切換 + 點擊日期開啟日曆彈窗（繁體中文）

import React, { useState } from 'react';
import { Box, IconButton, Button, Popover } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';

// 設定 dayjs 預設語系為繁體中文
dayjs.locale('zh-tw');

// 星期對照表
const WEEKDAY_MAP = ['日', '一', '二', '三', '四', '五', '六'];

/**
 * 單日日期導航器
 * 
 * @param {string} value - 當前日期 (YYYY-MM-DD)
 * @param {function} onChange - 日期變更回呼 (YYYY-MM-DD)
 * @param {string} [minDate] - 最小可選日期 (YYYY-MM-DD)
 * @param {string} [maxDate] - 最大可選日期，預設為今天
 * @param {boolean} [showIcon] - 是否顯示日曆圖示
 */
const SingleDateNavigator = ({
  value,
  onChange,
  minDate,
  maxDate,
  showIcon = true,
  fontSize,          // 日期文字字級（數字 px 或字串）；預設 0.875rem。給訂單管理跟全域「A」一起縮放。
  arrowSize,         // 左右箭頭 icon 字級（數字 px）；手機放大用。
  buttonSize = 'small',
  compact = false    // true → 短日期 M/D（X）省空間（手機用）；預設 YYYY/MM/DD
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentDate = dayjs(value);
  const today = dayjs();
  const maxDay = maxDate ? dayjs(maxDate) : today;
  const minDay = minDate ? dayjs(minDate) : null;

  // 格式化顯示：2026/02/04 (三)；compact → 2/4 (三)
  const displayText = compact
    ? `${currentDate.format('M/D')} (${WEEKDAY_MAP[currentDate.day()]})`
    : `${currentDate.format('YYYY/MM/DD')} (${WEEKDAY_MAP[currentDate.day()]})`;

  // 判斷箭頭是否可點擊
  const canGoPrev = minDay ? currentDate.isAfter(minDay, 'day') : true;
  const canGoNext = currentDate.isBefore(maxDay, 'day');

  // 切換到前一天
  const handlePrev = () => {
    const prevDate = currentDate.subtract(1, 'day');
    if (!minDay || prevDate.isAfter(minDay.subtract(1, 'day'), 'day')) {
      onChange(prevDate.format('YYYY-MM-DD'));
    }
  };

  // 切換到後一天
  const handleNext = () => {
    const nextDate = currentDate.add(1, 'day');
    if (nextDate.isBefore(maxDay.add(1, 'day'), 'day')) {
      onChange(nextDate.format('YYYY-MM-DD'));
    }
  };

  // 點擊日期文字 → 開啟日曆彈窗
  const handleOpenCalendar = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // 從日曆選擇日期
  const handleCalendarChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      onChange(newValue.format('YYYY-MM-DD'));
      setAnchorEl(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="zh-tw">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* 左箭頭 — 前一天 */}
        <IconButton
          size={buttonSize}
          onClick={handlePrev}
          disabled={!canGoPrev}
          sx={{ color: 'text.secondary' }}
        >
          <ChevronLeftIcon sx={arrowSize ? { fontSize: arrowSize } : undefined} />
        </IconButton>

        {/* 日期按鈕 — 點擊開啟日曆 */}
        <Button
          variant="outlined"
          size={buttonSize}
          onClick={handleOpenCalendar}
          startIcon={showIcon ? <CalendarIcon sx={arrowSize ? { fontSize: arrowSize } : undefined} fontSize="small" /> : undefined}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: fontSize || '0.875rem',
            color: 'text.primary',
            borderColor: 'divider',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            minWidth: 'auto',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {displayText}
        </Button>

        {/* 日曆彈窗 */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: { mt: 0.5, borderRadius: 2, boxShadow: 3 }
            }
          }}
        >
          <DateCalendar
            value={currentDate}
            onChange={handleCalendarChange}
            maxDate={maxDay}
            minDate={minDay || undefined}
          />
        </Popover>

        {/* 右箭頭 — 後一天 */}
        <IconButton
          size={buttonSize}
          onClick={handleNext}
          disabled={!canGoNext}
          sx={{ color: 'text.secondary' }}
        >
          <ChevronRightIcon sx={arrowSize ? { fontSize: arrowSize } : undefined} />
        </IconButton>
      </Box>
    </LocalizationProvider>
  );
};

export default SingleDateNavigator;